// booking.jsx
import { useEffect, useMemo, useState } from "react";
import { ACCESS_TOKEN } from "@/constants";
import { createPortal } from "react-dom";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Filter,
  Search,
  CircleStar,
  Pencil,
  Trash2,
  CircleHelp,
} from "lucide-react";

import BookingModal from "@/components/modal/BookingModal";
import DeleteConfirmDialog from "@/components/modal/DeleteConfirmDialog";
import BookingGuideModal from "@/components/modal/BookingGuideModal";

import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const API_URL = import.meta.env.VITE_API_URL;

function Booking({ onGoToAccommodations }) {
  const [bookings, setBookings] = useState(() => {
    try {
      const cached = sessionStorage.getItem("sda_bookings_cache");
      return cached ? JSON.parse(cached) : [];
    } catch (e) {
      return [];
    }
  });

  const [rawBookings, setRawBookings] = useState(() => {
    try {
      const cached = sessionStorage.getItem("sda_raw_bookings_cache");
      return cached ? JSON.parse(cached) : [];
    } catch (e) {
      return [];
    }
  });

  const [loading, setLoading] = useState(() => {
    try {
      const cached = sessionStorage.getItem("sda_bookings_cache");
      const arr = cached ? JSON.parse(cached) : [];
      return !(Array.isArray(arr) && arr.length > 0);
    } catch (e) {
      return true;
    }
  });
  const [error, setError] = useState(null);

  const [hasAccommodations, setHasAccommodations] = useState(true); // NEW: track if owner has listings
  const [showNoAccommodationModal, setShowNoAccommodationModal] =
    useState(false); // NEW: control info modal

  const getToken = () =>
    localStorage.getItem(ACCESS_TOKEN) || sessionStorage.getItem(ACCESS_TOKEN);

  // small helper: convert backend status to friendly label used in UI
  // backend examples: "pending", "confirmed", "cancelled", "completed"
  function normalizeStatus(statusRaw) {
    if (!statusRaw) return "Pending";
    const s = statusRaw.toString().toLowerCase();
    if (s.includes("confirm")) return "Approved";
    if (s.includes("pending")) return "Pending";
    if (s.includes("cancel")) return "Cancelled";
    if (s.includes("complete")) return "Completed";
    return capitalize(statusRaw);
  }

  function capitalize(str) {
    if (!str) return str;
    return String(str).charAt(0).toUpperCase() + String(str).slice(1);
  }

  // Fetch bookings and normalize shape to what the UI expects
  const fetchBookings = async () => {
    const hasCachedBookings = Array.isArray(bookings) && bookings.length > 0;
    if (!hasCachedBookings) setLoading(true);
    setError(null);
    try {
      const token = getToken();
      if (!token) {
        setError("No access token found. Please login.");
        setLoading(false);
        return;
      }

      const res = await fetch(`${API_URL}/api/sdaowner/get_bookings`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const data = await res.json();
      console.log("GET /get_bookings:", res.status, data);

      if (!res.ok) {
        const msg =
          data?.message || data?.msg || JSON.stringify(data) || "Unknown error";
        setError(msg);
        setLoading(false);
        return;
      }

      const raw = data?.bookings || [];
      setRawBookings(raw);
      sessionStorage.setItem("sda_raw_bookings_cache", JSON.stringify(raw));

      const normalized = raw.map((b) => {
        const rawStatus = (b?.status || "").toString();
        const status = normalizeStatus(rawStatus);

        const fmt = (iso) => {
          try {
            if (!iso) return "";
            const d = new Date(iso);
            if (isNaN(d)) return iso;
            return d.toLocaleDateString();
          } catch {
            return iso;
          }
        };

        return {
          id: b?.id ?? b?.Id ?? Math.random().toString(36).slice(2, 9),
          name: b?.clientName ?? b?.name ?? "Unknown",
          email: b?.clientEmail ?? b?.client_email ?? b?.email ?? "",
          phone: b?.clientPhone ?? b?.client_phone ?? b?.phone ?? "",
          accommodationTitle:
            b?.accommodationTitle ?? b?.accommodation_title ?? "—",
          location: b?.location ?? "—",
          checkIn: fmt(b?.checkIn ?? b?.check_in),
          checkOut: fmt(b?.checkOut ?? b?.check_out),
          status,
          _rawStatus: rawStatus,
        };
      });

      setBookings(normalized);
      sessionStorage.setItem("sda_bookings_cache", JSON.stringify(normalized));
    } catch (err) {
      console.error("Failed to fetch bookings:", err);
      setError(String(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBookings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // KPI values (computed from normalized bookings)
  const kpis = useMemo(() => {
    const total = bookings.length;
    const approved = bookings.filter(
      (b) => (b.status || "").toLowerCase() === "approved",
    ).length;
    const pending = bookings.filter(
      (b) => (b.status || "").toLowerCase() === "pending",
    ).length;
    const cancelled = bookings.filter(
      (b) => (b.status || "").toLowerCase() === "cancelled",
    ).length;
    return [
      { label: "Total Booking", value: total },
      { label: "Approved", value: approved },
      { label: "Pending", value: pending },
      { label: "Cancelled", value: cancelled },
    ];
  }, [bookings]);

  // UI state
  const [query, setQuery] = useState("");
  const [openFilter, setOpenFilter] = useState(false);
  const [openGuide, setOpenGuide] = useState(false);

  // applied status filter (used by the table)
  const [statusFilter, setStatusFilter] = useState("all");

  // temp selection inside the modal (only applied when clicking "Apply")
  const [pendingStatusFilter, setPendingStatusFilter] = useState("all");

  const [openModal, setOpenModal] = useState(false);
  const [selectedBook, setSelectedBook] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  // useEffect(() => {
  //   if (openAddFromDashboard) {
  //     // ensure it's in "add" mode
  //     setSelectedBook(null);
  //     setOpenModal(true);

  //   }
  // }, [openAddFromDashboard, onAddHandled]);

  const handleSearch = () => {
    console.log("Search clicked, query:", query);
    // local simple client-side filter example
    // you could call a server search endpoint instead
    if (!query) {
      fetchBookings();
      return;
    }
    const q = query.toLowerCase();
    setBookings((prev) =>
      prev.filter((b) => {
        return (
          (b.name || "").toLowerCase().includes(q) ||
          (b.accommodationTitle || "").toLowerCase().includes(q) ||
          (b.location || "").toLowerCase().includes(q)
        );
      }),
    );
  };

  const handleExport = () => {
    try {
      // Export what user currently sees (after Filters/Search)
      const rows = filteredBookings;

      const doc = new jsPDF({
        orientation: "landscape",
        unit: "pt",
        format: "a4",
      });

      const now = new Date();
      const generatedAt = now.toLocaleString();

      // Title
      doc.setFontSize(16);
      doc.text("Bookings", 40, 40);

      // Generated time
      doc.setFontSize(10);
      doc.text(`Generated: ${generatedAt}`, 40, 58);

      // KPI summary (same numbers shown in UI)
      const kpiText = kpis.map((k) => `${k.label}: ${k.value}`).join("   |   ");
      doc.text(kpiText, 40, 74);

      // Table
      autoTable(doc, {
        startY: 95,
        head: [
          [
            "Resident",
            "Email",
            "Phone",
            "Property",
            "Location",
            "Check in",
            "Check out",
            "Status",
          ],
        ],
        body: rows.map((b) => [
          b.name || "",
          b.email || "",
          b.phone || "",
          b.accommodationTitle || "",
          b.location || "",
          b.checkIn || "",
          b.checkOut || "",
          b.status || "",
        ]),
        styles: { fontSize: 9 },
        headStyles: { fillColor: [210, 19, 140] }, // matches your pink-ish theme
        margin: { left: 40, right: 40 },
      });

      const filename = `bookings_${now.toISOString().slice(0, 10)}.pdf`;
      doc.save(filename);
    } catch (err) {
      console.error("Export PDF failed:", err);
      setError("Export PDF failed. Please try again.");
    }
  };

  const handleAddNew = () => {
    // NEW: if owner has no accommodations, show info modal instead of opening booking modal
    if (!hasAccommodations) {
      setShowNoAccommodationModal(true);
      return;
    }

    // Original behavior when there ARE accommodations
    setSelectedBook(null);
    setOpenModal(true);
  };

  const handleEdit = (displayBooking) => {
    const raw = rawBookings.find(
      (r) => String(r.id) === String(displayBooking.id),
    );
    setSelectedBook(raw || null);
    setOpenModal(true);
  };

  const handleDelete = (booking) => {
    setSelectedId(booking.id);
    setOpenDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    try {
      const token = getToken();
      if (!token) {
        console.error("No access token found for delete");
        return;
      }

      const res = await fetch(
        `${API_URL}/api/sdaowner/delete_booking/${selectedId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      const data = await res.json();

      if (res.ok) {
        setBookings((prev) => {
          const next = prev.filter((acc) => acc.id !== selectedId);
          sessionStorage.setItem("sda_bookings_cache", JSON.stringify(next));
          return next;
        });

        setRawBookings((prev) => {
          const next = prev.filter((r) => String(r.id) !== String(selectedId));
          sessionStorage.setItem(
            "sda_raw_bookings_cache",
            JSON.stringify(next),
          );
          return next;
        });
      } else {
        console.error(
          "Delete failed:",
          data.message || data.msg || "Unknown error",
        );
      }
    } catch (error) {
      console.error("Error deleting book:", error);
    } finally {
      setOpenDeleteDialog(false);
      setSelectedId(null);
    }
  };

  // status badge helper (uses normalized statuses)
  const statusDisplay = (status) => {
    const normalized = (status || "").toString().toLowerCase();
    const colors = {
      approved: "bg-green-500",
      pending: "bg-yellow-400",
      cancelled: "bg-red-500",
      completed: "bg-blue-400",
    };
    const colorClass = colors[normalized] || "bg-gray-300";
    return (
      <div className="flex items-center gap-2 justify-center">
        <span className={`h-3 w-3 rounded-full ${colorClass}`} />
        <span className="text-gray-700">{status}</span>
      </div>
    );
  };

  const filteredBookings = useMemo(() => {
    if (statusFilter === "all") return bookings;

    const target = statusFilter.toLowerCase();
    return bookings.filter((b) => (b.status || "").toLowerCase() === target);
  }, [bookings, statusFilter]);

  return (
    <div className="space-y-6">
      {/* Header: KPIs on the LEFT, Title + Create button on the RIGHT */}
      <div className="w-full flex flex-col md:flex-row items-stretch md:items-center justify-between gap-4">
        <div className="flex items-center justify-center gap-2 md:ml-6 w-full md:w-auto">
          <Button
            onClick={handleAddNew}
            className="bg-[#D2138C] hover:bg-pink-700 rounded-full text-white px-4 py-2 w-auto">
            Create Booking
          </Button>

          {/* Mobile-only Guide button beside Create */}
          <Button
            variant="outline"
            onClick={() => setOpenGuide(true)}
            className="md:hidden shrink-0 rounded-full border-[#D2138C] text-[#D2138C] hover:bg-pink-50 hover:text-[#D2138C] h-11 w-11 p-0"
            aria-label="Booking guide"
            title="Booking guide">
            <CircleHelp className="h-4 w-4" />
          </Button>
        </div>

        <div className="w-full md:w-auto">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 md:flex md:gap-0">
            {kpis.map((k) => (
              <Card
                key={k.label}
                className="bg-white border shadow-sm rounded-xl md:bg-transparent md:shadow-none md:border-0 md:rounded-none">
                <CardContent className="p-2 md:p-0">
                  <div className="flex flex-col items-center justify-center px-2 py-2 md:px-4 md:py-2">
                    <div className="text-xs font-medium text-muted-foreground">
                      {k.label}
                    </div>
                    <div className="text-2xl font-semibold">{k.value}</div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>

      {/* Search and filters */}
      <div className="w-full flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div className="flex w-full md:w-[520px] max-w-3xl items-center gap-2 md:gap-3 px-0 md:px-3 py-1">
          <Input
            type="text"
            placeholder="Search"
            className="border-none bg-white focus-visible:ring-0 text-gray-700 h-11"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <Button
            onClick={handleSearch}
            className="bg-[#D2138C] hover:bg-pink-700 rounded-full text-white px-4 h-11 whitespace-nowrap">
            <Search className="h-4 w-4 mr-2" /> Search
          </Button>
        </div>

        <div className="w-full md:w-auto">
          {/* Filters + Export */}
          <div className="grid grid-cols-2 gap-2 sm:flex sm:justify-end sm:items-center sm:gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setPendingStatusFilter(statusFilter);
                setOpenFilter(true);
              }}
              className="rounded-full border-[#D2138C] text-[#D2138C] hover:bg-pink-50 hover:text-[#D2138C] px-3 w-full sm:w-auto">
              <Filter className="h-4 w-4 mr-2" /> Filters
            </Button>

            <Button
              onClick={handleExport}
              className="rounded-full text-white bg-[#D2138C] hover:bg-pink-700 px-3 w-full sm:w-auto">
              <CircleStar className="h-4 w-4 mr-2" /> Export PDF
            </Button>

            {/* Desktop-only Guide button aligned with Filters/Export */}
            <Button
              variant="outline"
              onClick={() => setOpenGuide(true)}
              className="hidden md:inline-flex rounded-full border-[#D2138C] text-[#D2138C] hover:bg-pink-50 hover:text-[#D2138C] h-11 w-11 p-0"
              aria-label="Booking guide"
              title="Booking guide">
              <CircleHelp className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* error / loading */}
      {error && (
        <div className="text-red-600 bg-red-50 p-2 rounded">{error}</div>
      )}
      {loading && (
        <div className="text-sm text-gray-600">Loading bookings…</div>
      )}

      {/* Mobile cards (smaller screens) */}
      <div className="md:hidden space-y-3">
        {!loading && filteredBookings.length === 0 ? (
          <div className="text-center text-muted-foreground bg-white border rounded-xl p-4">
            no bookings found
          </div>
        ) : (
          filteredBookings.map((b) => (
            <div
              key={b.id}
              className="bg-white border rounded-xl shadow-sm p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-semibold text-gray-900 truncate">
                    {b.name}
                  </div>
                  <div className="text-xs text-gray-500 truncate">
                    {b.email}
                  </div>
                  <div className="text-xs text-gray-500 truncate">
                    {b.phone}
                  </div>
                </div>

                <div className="shrink-0">{statusDisplay(b.status)}</div>
              </div>

              <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                <div className="bg-gray-50 rounded-lg p-2">
                  <div className="text-xs text-gray-500">Property</div>
                  <div className="text-gray-800 truncate">
                    {b.accommodationTitle}
                  </div>
                </div>
                <div className="bg-gray-50 rounded-lg p-2">
                  <div className="text-xs text-gray-500">Location</div>
                  <div className="text-gray-800 truncate">{b.location}</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-2">
                  <div className="text-xs text-gray-500">Check in</div>
                  <div className="text-gray-800">{b.checkIn || "—"}</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-2">
                  <div className="text-xs text-gray-500">Check out</div>
                  <div className="text-gray-800">{b.checkOut || "—"}</div>
                </div>
              </div>

              <div className="mt-3 flex justify-end gap-2">
                <Button
                  onClick={() => handleEdit(b)}
                  size="sm"
                  variant="outline"
                  className="rounded-full border-gray-200">
                  <Pencil className="h-4 w-4 mr-2" /> Edit
                </Button>

                <Button
                  onClick={() => handleDelete(b)}
                  size="sm"
                  variant="outline"
                  className="rounded-full border-[#D2138C] text-[#D2138C] hover:bg-pink-50">
                  <Trash2 className="h-4 w-4 mr-2 text-red-500" /> Delete
                </Button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Desktop table (md and up) */}
      <div className="hidden md:block rounded-lg border overflow-x-auto shadow-md">
        <Table className="">
          <TableHeader>
            <TableRow className="bg-gray-50">
              <TableHead className="text-black text-center">Resident</TableHead>
              <TableHead className="text-black text-center">Email</TableHead>
              <TableHead className="text-black text-center">Phone</TableHead>
              <TableHead className="text-black text-center">Property</TableHead>
              <TableHead className="text-black text-center">Location</TableHead>
              <TableHead className="text-black text-center">Check in</TableHead>
              <TableHead className="text-black text-center">
                Check out
              </TableHead>
              <TableHead className="text-black text-center">Status</TableHead>
              <TableHead className="text-black text-center">Action</TableHead>
            </TableRow>
          </TableHeader>

          <TableBody className="bg-white">
            {!loading && filteredBookings.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={9}
                  className="text-center text-muted-foreground">
                  no bookings found
                </TableCell>
              </TableRow>
            ) : (
              filteredBookings.map((b) => (
                <TableRow key={b.id} className="text-gray-700">
                  <TableCell className="text-center font-medium">
                    {b.name}
                  </TableCell>
                  <TableCell className="text-center font-medium">
                    {b.email}
                  </TableCell>
                  <TableCell className="text-center font-medium">
                    {b.phone}
                  </TableCell>
                  <TableCell className="text-center">
                    {b.accommodationTitle}
                  </TableCell>
                  <TableCell className="text-center">{b.location}</TableCell>
                  <TableCell className="text-center">{b.checkIn}</TableCell>
                  <TableCell className="text-center">{b.checkOut}</TableCell>
                  <TableCell className="text-center">
                    {statusDisplay(b.status)}
                  </TableCell>
                  <TableCell className="text-center">
                    <div className="flex items-center justify-center gap-2">
                      <Button
                        onClick={() => handleEdit(b)}
                        size="sm"
                        variant="ghost"
                        className="text-gray-600 hover:bg-gray-100">
                        <Pencil className="h-4 w-4 text-gray-600" />
                      </Button>
                      <Button
                        onClick={() => handleDelete(b)}
                        size="sm"
                        variant="ghost"
                        className="text-[#D2138C] hover:bg-[#ffedf4]">
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Status Filter Modal */}
      {openFilter &&
        createPortal(
          <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
              <h3 className="text-lg font-semibold mb-4">Filter by status</h3>

              <div className="space-y-2 mb-6">
                {/* All */}
                <button
                  type="button"
                  onClick={() => setPendingStatusFilter("all")}
                  className={`w-full text-left px-4 py-2 rounded-lg border ${
                    pendingStatusFilter === "all"
                      ? "border-[#D2138C] bg-pink-50 text-[#D2138C] font-medium"
                      : "border-gray-200 text-gray-700 hover:bg-gray-50"
                  }`}>
                  All
                </button>

                {/* Pending */}
                <button
                  type="button"
                  onClick={() => setPendingStatusFilter("pending")}
                  className={`w-full text-left px-4 py-2 rounded-lg border ${
                    pendingStatusFilter === "pending"
                      ? "border-[#D2138C] bg-pink-50 text-[#D2138C] font-medium"
                      : "border-gray-200 text-gray-700 hover:bg-gray-50"
                  }`}>
                  Pending
                </button>

                {/* Approved */}
                <button
                  type="button"
                  onClick={() => setPendingStatusFilter("approved")}
                  className={`w-full text-left px-4 py-2 rounded-lg border ${
                    pendingStatusFilter === "approved"
                      ? "border-[#D2138C] bg-pink-50 text-[#D2138C] font-medium"
                      : "border-gray-200 text-gray-700 hover:bg-gray-50"
                  }`}>
                  Approved
                </button>

                {/* Cancelled */}
                <button
                  type="button"
                  onClick={() => setPendingStatusFilter("cancelled")}
                  className={`w-full text-left px-4 py-2 rounded-lg border ${
                    pendingStatusFilter === "cancelled"
                      ? "border-[#D2138C] bg-pink-50 text-[#D2138C] font-medium"
                      : "border-gray-200 text-gray-700 hover:bg-gray-50"
                  }`}>
                  Cancelled
                </button>
              </div>

              <div className="flex justify-end gap-2">
                <Button
                  onClick={() => {
                    setStatusFilter(pendingStatusFilter);
                    setOpenFilter(false);
                  }}
                  className="bg-[#D2138C] hover:bg-pink-700 text-white rounded-full">
                  Apply
                </Button>
              </div>
            </div>
          </div>,
          document.body,
        )}

      {/* NEW: No Accommodations Modal */}
      {showNoAccommodationModal && (
        <div className="fixed inset-0 z-[9998] flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold mb-2">
              No accommodations listed
            </h3>
            <p className="text-sm text-gray-600 mb-6">
              You currently don&apos;t have any listed accommodations. Please
              list an accommodation first before creating a booking.
            </p>

            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                className="rounded-full"
                onClick={() => setShowNoAccommodationModal(false)}>
                Close
              </Button>
              <Button
                className="bg-[#D2138C] hover:bg-pink-700 text-white rounded-full"
                onClick={() => {
                  setShowNoAccommodationModal(false);
                  if (onGoToAccommodations) {
                    onGoToAccommodations(); // this tells Portal.jsx to switch to Manage Accommodations
                  }
                }}>
                List an accommodation
              </Button>
            </div>
          </div>
        </div>
      )}

      <BookingGuideModal open={openGuide} onClose={() => setOpenGuide(false)} />

      <BookingModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        booking={selectedBook}
        onSuccess={fetchBookings}
      />

      <DeleteConfirmDialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Book"
        description="Are you sure you want to delete this book? This action cannot be undone."
      />
    </div>
  );
}

export default Booking;
